import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import PhoneMockup from "./PhoneMockup";
import { useSite } from "@/lib/site";
import { mediaUrl } from "@/lib/api";

const NUM = {
  ar: ["٠١", "٠٢", "٠٣", "٠٤", "٠٥", "٠٦", "٠٧", "٠٨"],
  fr: ["01", "02", "03", "04", "05", "06", "07", "08"],
};

export default function Gallery({ content, screenshots }) {
  const { lang } = useSite();
  const shots = screenshots || [];
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const c = content?.gallery;
  const isFr = lang === "fr";
  const nums = NUM[lang] || NUM.ar;
  const shotTitle = (s) => (isFr ? s?.title_fr || s?.title : s?.title);

  if (!shots.length) {
    return (
      <section id="gallery" className="grain relative overflow-hidden bg-forest-950 py-24 sm:py-28">
        <div className="mx-auto max-w-3xl px-5">
          <SectionHeading dark eyebrow={c?.eyebrow} title={c?.title} subtitle={c?.subtitle} />
          <div className="mt-12 rounded-3xl border border-dashed border-gold/30 p-14 text-center text-sm text-cream/60" data-testid="gallery-empty">
            {isFr ? "Les captures d'écran seront bientôt disponibles" : "لقطات التطبيق ستُضاف قريباً"}
          </div>
        </div>
      </section>
    );
  }

  const shot = shots[Math.min(active, shots.length - 1)];
  const go = (d) => setActive((a) => (a + d + shots.length) % shots.length);

  return (
    <section id="gallery" className="grain relative overflow-hidden bg-forest-950 py-24 sm:py-28">
      <div className="pointer-events-none absolute -top-24 right-1/3 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5">
        <SectionHeading dark eyebrow={c?.eyebrow} title={c?.title} subtitle={c?.subtitle} />

        <div className="mt-16 grid items-center gap-12 lg:grid-cols-[auto_1fr] lg:gap-20">
          <Reveal className="relative">
            <div className="scene-3d relative mx-auto w-fit">
              <div className="absolute -inset-10 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={shot.id || active}
                  initial={{ opacity: 0, x: reduce ? 0 : 46, rotateY: reduce ? 0 : -14 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0 }}
                  exit={{ opacity: 0, x: reduce ? 0 : -46, rotateY: reduce ? 0 : 14 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  style={{ transformStyle: "preserve-3d" }}
                  data-testid="gallery-featured-phone"
                >
                  <PhoneMockup shot={shot} width={290} caption={false} />
                </motion.div>
              </AnimatePresence>
              <div className="mx-auto -mt-4 h-12 w-2/3 rounded-[100%] bg-gold/15 blur-2xl" aria-hidden="true" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="text-center lg:text-right">
              <p className="font-head text-5xl font-black text-gold/25" data-testid="gallery-counter">
                {nums[active] || active + 1}
                <span className="text-xl text-cream/30"> / {nums[shots.length - 1] || shots.length}</span>
              </p>
              <AnimatePresence mode="wait" initial={false}>
                <motion.h3
                  key={shot.id || active}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  className="mt-4 font-head text-2xl font-extrabold text-cream sm:text-3xl"
                  data-testid="gallery-screen-title"
                >
                  {shotTitle(shot)}
                </motion.h3>
              </AnimatePresence>

              <div className="mt-8 flex items-center justify-center gap-3 lg:justify-start" data-testid="gallery-controls">
                <button
                  onClick={() => go(-1)}
                  data-testid="gallery-prev-button"
                  aria-label={isFr ? "Écran précédent" : "اللقطة السابقة"}
                  className="rounded-full border border-cream/20 p-3 text-cream transition hover:border-gold hover:text-gold"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2">
                  {shots.map((s, i) => (
                    <button
                      key={s.id || i}
                      onClick={() => setActive(i)}
                      aria-label={isFr ? `Écran ${i + 1}` : `اللقطة ${i + 1}`}
                      data-testid={`gallery-dot-${i}`}
                      className={`h-2.5 rounded-full transition-all duration-300 ${i === active ? "w-8 bg-gold" : "w-2.5 bg-cream/25 hover:bg-cream/50"}`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => go(1)}
                  data-testid="gallery-next-button"
                  aria-label={isFr ? "Écran suivant" : "اللقطة التالية"}
                  className="rounded-full border border-cream/20 p-3 text-cream transition hover:border-gold hover:text-gold"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start" data-testid="gallery-thumbnails">
                {shots.map((s, i) => (
                  <button
                    key={s.id || i}
                    onClick={() => setActive(i)}
                    data-testid={`gallery-thumb-${i}`}
                    aria-label={shotTitle(s)}
                    className={`overflow-hidden rounded-xl border-2 transition-all duration-300 ${
                      i === active ? "border-gold shadow-[0_0_24px_-6px_rgba(212,175,55,0.6)]" : "border-transparent opacity-50 hover:opacity-90"
                    }`}
                  >
                    <img src={mediaUrl(s.media_id)} alt={shotTitle(s)} loading="lazy" className="h-24 w-12 object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
